import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid-pro';
import { useGridApiRef, getGridStringOperators } from '@mui/x-data-grid';

/** Redux Packages. */
import { useDispatch, useSelector, useStore } from 'react-redux';

/** Redux Reducers. */
import { setSelectValueChange } from 'shared/store/slices/selectionSlice';
import { updateFilterView } from 'shared/store/slices/filterViewSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { gridSummariesReceived } from 'shared/store/slices/shipmentTodoSlice';

/** Internal Components. */
import { handleTabKeyDown, StripedDataGrid } from 'shared/ui/Datagrid';
import { readSocketData, updateSocketData } from 'shared/lib/fetchSocket';
import { formatDateToDateString, formatDateToISOString } from 'shared/lib/dateFormatter';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';

import EmailBuilderDialog from './EmailTemplate/TemplateBuilder';
import ShipmentDetail from './ShipmentDetail';
import { syncReadCacheToConnection } from '../lib/shipmentReadCache';
import { buildCompleteReadPayload, completeWindowKey, pruneCompleteRows } from '../lib/completeDateWindow';
import Comment from './Comment/Comment';
import MainHistoryDialog from './MainHistory/MainHistoryDialog';
import { reducerList, getColumnsByType } from 'data/shipmentMainTableReducers';
import { complexValueSortComparator, isEmptyComplexValue } from 'shared/lib/quickFilterUtils';
import { buildShipmentRows } from '../lib/buildShipmentRows';

/** Baseline row height when every stacked column fits in one line. */
const ROW_BASE_HEIGHT = 55;
/** Estimated height of one stacked line (11px Typography at default line height).
 * Estimates err slightly low on purpose: measuring taller than estimated extends
 * the scroll length benignly, while shrinking it clamps the scroll position. */
const ROW_LINE_HEIGHT = 16;

/** 배열/객체 값 컬럼에서도 isEmpty/isNotEmpty가 올바르게 동작하도록 기본 string operator를 패치. */
const complexValueFilterOperators = getGridStringOperators().map((operator) =>
    operator.value === 'isEmpty' || operator.value === 'isNotEmpty'
        ? {
              ...operator,
              getApplyFilterFn: () => (value) =>
                  operator.value === 'isEmpty' ? isEmptyComplexValue(value) : !isEmptyComplexValue(value),
          }
        : operator
);

export default function ShipmentMainTable(props) {
    /**
     * Get props from Shipments.js.
     * onReadSettled(component, connectionEpoch) tells the page this grid's rows are on screen, so the side panel read can start.
     */
    const { mainConfig, hasReadData, onReadSettled } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();
    /** Store handle for reading the lists as they are when a date window is swapped out. */
    const store = useStore();

    /** Create a reference to the Datagrid API.  */
    const apiRef = useGridApiRef();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);

    /** Redux states for different parts of the ocean import shipment. */
    const shipmentList = useSelector((state) => state[mainConfig.formState[0]]);
    const hblShipmentList = useSelector((state) => state[mainConfig.formState[1]]);
    const cntrShipmentList = useSelector((state) => state[mainConfig.formState[2]]);
    const splitShipmentList = useSelector((state) => {
        const key = mainConfig.formState[3];
        if (!key) return [];
        return state[key] ?? [];
    });
    const statusList = useSelector((state) => state[mainConfig.formState[4]]);
    const commentList = useSelector((state) => state[mainConfig.formState[5]]);

    /**
     * Additional state selectors:
     * - shipmentType: Type of shipment being processed.
     * - selectValue: Currently selected value in a datagrid.
     * - filterView: Data related to applied filters.
     */
    const shipmentType = useSelector((state) => state.progressValue);
    const selectValue = useSelector((state) => state.mainTableSelectValue);
    const filterView = useSelector((state) => state.filterViewData);
    /** All todo list from redux state. */
    const todoSummary = useSelector((state) => state.shipmentTodo.summaries);
    /** Socket connection status; wait for connection before emitting. */
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);
    /** Bumps on every (re)connection; a grid read on an older connection is re-read. */
    const connectionEpoch = useSelector((state) => state.socketStatus.connectionEpoch);
    /** Date window the COMPLETE grid is read with; null ends mean the server default (past year). */
    const completeDateWindow = useSelector((state) => state.completeDateWindow);

    /** State to track the currently selected row in the DataGrid. */
    const [selectedRow, setSelectedRow] = useState(null);
    /** State to store dropdown options for trade partners, locations, and vessels. */
    const [tradePartnerOptions, setTradePartnerOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [vesselOptions, setVesselOptions] = useState([]);

    /** State for managing a email template dialog. */
    const [emailTemplateOpen, setEmailTemplateOpen] = useState(false);
    /** State for managing a commnet dialog. */
    const [commentOpen, setCommentOpen] = useState(false);
    /** State for managing a history dialog. */
    const [historyOpen, setHistoryOpen] = useState(false);

    /** State to manage the IDs of rows with open detail panels in the DataGrid. */
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);

    /** State to display loading component until API response is received. */
    const [loaded, setLoaded] = useState(false);
    const [gridLoading, setGridLoading] = useState(false);

    /** Function to dynamically set the height of the detail panel. */
    const getDetailPanelHeight = useCallback(() => 'auto', []);

    /**
     * Hook to fetch and set shipment data from socket (only after socket is connected).
     * After a reconnection the "already read" marks are wiped first, so the grid — and
     * the status/rate/comment/dispute rows its detail panels show, which arrive in the
     * same bundle — is re-read and edits made by teammates during the outage appear
     * without a page refresh.
     *
     * COMPLETE rows are read for one date window at a time (OS-587). When the operator
     * picks another window, the rows of the old window — and their houses, containers,
     * statuses, rates, comments and disputes — are dropped from the store before the new
     * window is read, since the Complete reducers merge into the In Progress lists.
     *
     * The same read carries each row's todo badge counts, so badges appear with the rows
     * instead of waiting for every todo card of the tab. A reply that arrives after a
     * reconnection is dropped because the new connection reads again.
     *
     * Only the latest read of a grid is applied. If the operator switches between IN
     * PROGRESS and COMPLETE or picks another window while a read is still out, the slower
     * earlier reply would otherwise empty the screen or mix rows of two windows. Going back
     * to a view that is already loaded also cancels the read still out.
     *
     * IN PROGRESS and COMPLETE share one row list: an IN PROGRESS read replaces it and a
     * COMPLETE read adds to it. So after an IN PROGRESS reply the COMPLETE rows are gone
     * and COMPLETE is read again on the next visit.
     *
     * When the rows are on screen (or the read has failed) the page is told, and only then
     * reads the side panel. Both reads share one server process, so a panel read sent at the
     * same time held the first rows back by about as long as the panel itself took.
     */
    useEffect(() => {
        if (!socketConnected) return;

        syncReadCacheToConnection(hasReadData.current, connectionEpoch);

        const currentKey = mainConfig.component;
        const effectiveShipmentType = shipmentType === 'dispute' ? 'inprogress' : shipmentType;
        const tableCache = hasReadData.current.tables[currentKey];
        const isComplete = shipmentType === 'complete';
        const windowKey = completeWindowKey(completeDateWindow);

        if (isComplete && tableCache?.complete && tableCache.completeWindow !== windowKey) {
            const pruned = pruneCompleteRows(store.getState(), mainConfig);
            if (pruned) {
                Object.entries(pruned).forEach(([tableKey, rows]) => {
                    const reducerFunction = reducerList[mainConfig.reducerMap[tableKey]];
                    if (reducerFunction) dispatch(reducerFunction(rows));
                });
            }
            tableCache.complete = false;
        }

        const alreadyFetched = tableCache?.[effectiveShipmentType];

        if (alreadyFetched === undefined) {
            onReadSettled?.(mainConfig.component, connectionEpoch);
            return setLoaded(true);
        }
        if (alreadyFetched === true) {
            // The rows for this view are already loaded, so a read still out for the other view must not replace them.
            tableCache.latestRead = null;
            setGridLoading(false);
            onReadSettled?.(mainConfig.component, connectionEpoch);
            return setLoaded(true);
        }

        const sendValue = { ...(isComplete ? buildCompleteReadPayload(completeDateWindow) : {}), todo_read_model: true };
        const startedRevision = store.getState().shipmentTodo.revision;
        const reducerMap = isComplete ? mainConfig.reducerCompleteMap : mainConfig.reducerMap;
        const readToken = {};
        tableCache.latestRead = readToken;
        const isLatestRead = () => hasReadData.current.tables[currentKey]?.latestRead === readToken;

        setGridLoading(true);
        readSocketData(
            dispatch,
            tabValue,
            'row',
            mainConfig.component,
            null,
            sendValue,
            (receivedData) => {
                if (store.getState().socketStatus.connectionEpoch !== connectionEpoch) return;
                if (!isLatestRead()) return;
                dispatch(gridSummariesReceived({ tab: tabValue, receivedData,
                    masterTable: mainConfig.tableName[0], startedRevision, isComplete }));
                mainConfig.tableName.forEach((tableKey) => {
                    const reducerName = reducerMap[tableKey];
                    const reducerFunction = reducerList[reducerName];
                    if (reducerFunction && receivedData[tableKey]) {
                        dispatch(reducerFunction(receivedData[tableKey]));
                    }
                });
                const readMarks = hasReadData.current.tables[currentKey];
                readMarks[shipmentType] = true;
                if (isComplete) readMarks.completeWindow = windowKey;
                // An IN PROGRESS reply replaces the whole list, completed rows included, so COMPLETE is read again.
                else readMarks.complete = false;
            },
            () => {},
            () => {
                if (!isLatestRead()) return;
                setLoaded(true);
                setGridLoading(false);
                onReadSettled?.(mainConfig.component, connectionEpoch);
            }
        );
    }, [tabValue, shipmentType, socketConnected, connectionEpoch, completeDateWindow, dispatch, store, hasReadData, mainConfig, onReadSettled]);

    /** Reset detailPanelExpandedRowIds and set new values when shipmentType changes. */
    useEffect(() => {
        setDetailPanelExpandedRowIds([]);
    }, [shipmentType]);

    /** Computed rows for DataGrid (memoized to avoid unnecessary reference changes and re-renders). */
    const shipmentRow = useMemo(
        () =>
            buildShipmentRows({
                shipmentList,
                hblShipmentList,
                cntrShipmentList,
                splitShipmentList,
                statusList,
                mainConfig,
                tabValue,
                shipmentType,
            }),
        [shipmentList, shipmentType, hblShipmentList, cntrShipmentList, splitShipmentList, statusList, mainConfig, tabValue]
    );

    const getDetailPanelContent = useCallback(
        ({ row }) => {
            return <ShipmentDetail rowData={row} apiRef={apiRef} />;
        },
        [shipmentRow]
    );

    /** Handle to change a selected row ID. */
    const handleSelectionChange = useCallback(
        (id) => {
            dispatch(setSelectValueChange(id));
        },
        [dispatch]
    );

    /** Handle to update a row in main table. */
    const handleProcessRowUpdate = useCallback(
        (newRow) => {
            const dateTimeFields = [
                'registered_on',
                'modified_on',
                'pick_appointment',
                'prepull_appointment',
                'delivery_appointment',
            ];

            const formatDateToString = (date) => formatDateToDateString(date);
            const formattedNewRow = {
                ...newRow,
                ...mainConfig.dateFormat.reduce((acc, field) => {
                    if (dateTimeFields.includes(field)) {
                        acc[field] = newRow[field] instanceof Date ? newRow[field].toISOString() : newRow[field];
                    } else {
                        acc[field] = formatDateToString(newRow[field]);
                    }
                    return acc;
                }, {}),
            };
            const oldRow = shipmentList.find((row) => row.id === newRow.id);
            const changes = Object.keys(formattedNewRow).reduce((acc, key) => {
                if (JSON.stringify(formattedNewRow[key]) !== JSON.stringify(oldRow[key])) {
                    acc[key] = formattedNewRow[key];
                }
                return acc;
            }, {});
            const excludedKeys = [
                ...mainConfig.stringColumns,
                '__quick_search',
                'alias_name',
                'alias_bol_number',
                'alias_il_location_id_deliver_to',
                'alias_il_trade_partner_id',
            ];
            const filteredChanges = Object.keys(changes).filter((key) => !excludedKeys.includes(key));
            if (filteredChanges.length === 0) return Promise.resolve(newRow);

            const key = filteredChanges[0];
            let value = changes[key];
            if (value === undefined) value = null;

            const isCaseClosureComplete = oldRow?.case_closure === 'Complete';
            if (isCaseClosureComplete && key !== 'case_closure') {
                dispatch(
                    setSnackbar({
                        open: true,
                        message:
                            'Cannot modify fields when Case Closure is Complete. Please change Case Closure first.',
                        severity: 'warning',
                    })
                );
                return Promise.resolve(oldRow);
            }

            const routeKeys = [
                'il_location_id_pol',
                'il_location_id_pod',
                'il_location_id_aol',
                'il_location_id_aod',
                'etd',
                'eta',
            ];
            let isRouteKey = routeKeys.includes(key);
            if (tabValue === 4 && (key === 'etd' || key === 'eta')) isRouteKey = false;

            const revertToOldValue = () => {
                if (apiRef.current && typeof apiRef.current.updateRows === 'function') {
                    apiRef.current.updateRows([{ id: formattedNewRow.id, [key]: oldRow[key] }]);
                }
            };

            const emitUpdate = (routeOverride = isRouteKey) => {
                const emitObj = {
                    id: formattedNewRow.id,
                    key,
                    value,
                    table_id: [],
                    route: routeOverride,
                };

                updateSocketData(
                    dispatch,
                    tabValue,
                    'cell',
                    mainConfig.tableName[0],
                    mainConfig.reducerActions[0],
                    emitObj,
                    (response) => {
                        const { route = true, message = '' } = response || {};
                        if (route === false) {
                            const retry = window.confirm(message);
                            if (retry) {
                                emitUpdate(false);
                            } else {
                                revertToOldValue(); // 사용자가 재시도 취소 → 되돌림
                            }
                            return;
                        }
                        // route === true (정상 저장): feta/deta 등은 소켓 브로드캐스트 → Redux 경로가 알아서 반영
                    },
                    () => {
                        revertToOldValue();
                    }
                );
            };

            emitUpdate();

            // 서버 응답(ack)을 기다리지 않고, 사용자가 입력한 값으로 즉시 그리드에 낙관적 커밋.
            // 성공 시 Redux(소켓 브로드캐스트) 경로가 최종값으로 다시 갱신하고,
            // 실패 시 revertToOldValue()가 Redux를 되돌려 그리드도 자동으로 원복됨.
            return Promise.resolve(formattedNewRow);
        },
        [dispatch, mainConfig, shipmentList, tabValue]
    );

    /** Handler for update datagrid filter/sort/visibility state. */
    const updateDataGridState = useCallback(
        (key, value) => {
            const dateTimeFields = [
                'registered_on',
                'modified_on',
                'pick_appointment',
                'prepull_appointment',
                'delivery_appointment',
            ];
            
            const formatDateToString = (date) => formatDateToISOString(date).slice(0, 10);
            const convertDateIfNeeded = (item) => {
                if (
                    mainConfig.dateFormat.includes(item.field) &&
                    item.value != undefined &&
                    typeof item.value !== 'string'
                ) {
                    let convertedValue;
                    if (dateTimeFields.includes(item.field)) {
                        convertedValue = item.value.toISOString();
                    } else {
                        convertedValue = formatDateToString(item.value);
                    }
                    return { ...item, value: convertedValue };
                }
                return item;
            };

            const updatedItems = value.items?.map(convertDateIfNeeded);
            const quickFilterItems = value.quickFilterValues;

            const payload =
                key === 'filterModel'
                    ? {
                          [key]: {
                              items: updatedItems,
                              logicOperator: value.logicOperator,
                              quickFilterValues: quickFilterItems ? quickFilterItems : [],
                          },
                      }
                    : { [key]: value };
            dispatch(updateFilterView(payload));
        },
        [dispatch, mainConfig]
    );

    /** Utility function to emit socket requests and handle responses. */
    const fetchSocketData = useCallback(
        (entity, reducer, searchValue, callback) => {
            const notNullValue = searchValue || '';
            readSocketData(dispatch, tabValue, 'cell', entity, reducer, notNullValue, (receivedData) => {
                callback(receivedData);
            });
        },
        [dispatch, tabValue]
    );

    const handleTradePartnerChange = useCallback(
        (event, value, params) => {
            const type = params.field;
            fetchSocketData('il_trade_partner', type, value, setTradePartnerOptions);
        },
        [fetchSocketData]
    );

    const handleLocationChange = useCallback(
        (event, value) => {
            fetchSocketData('il_location', '', value, setLocationOptions);
        },
        [fetchSocketData]
    );

    const handleVesselChange = useCallback(
        (event, value) => {
            fetchSocketData('il_opus_vessel', '', value, setVesselOptions);
        },
        [fetchSocketData]
    );

    const handleCellEditStart = useCallback(
        (params) => {
            const { field, value } = params;
            const locationFields = mainConfig.locationField;
            if (locationFields.includes(field)) {
                if (!value) {
                    setVesselOptions([]);
                }
                fetchSocketData('il_location', '', value?.name ?? '', setLocationOptions);
            } else if (field === 'il_opus_vessel_id') {
                fetchSocketData('il_opus_vessel', '', value, setVesselOptions);
            } else if (field.startsWith('il_trade_partner_id')) {
                fetchSocketData('il_trade_partner', field, value?.name ?? '', setTradePartnerOptions);
            }
        },
        [mainConfig.locationField, fetchSocketData]
    );

    const handleEmailTemplateOpen = useCallback((id, row) => {
        setEmailTemplateOpen(true);
        setSelectedRow([row]);
    }, []);

    const handleCommentOpen = useCallback((id, row) => {
        setCommentOpen(true);
        setSelectedRow([row]);
    }, []);

    const handleHistoryOpen = useCallback((id, row) => {
        setHistoryOpen(true);
        setSelectedRow([row]);
    }, []);

    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
        setDetailPanelExpandedRowIds((prevIds) => {
            // 리렌더링 방지를 위해 detail panel은 한 번에 하나만 열리도록 제한.
            // 새로 열린 row가 있다면 그것만 남기고, 그렇지 않으면(=닫힘) newIds를 그대로 반영.
            const newlyOpenedIds = newIds.filter((id) => !prevIds.includes(id));
            return newlyOpenedIds.length > 0 ? [newlyOpenedIds[newlyOpenedIds.length - 1]] : newIds;
        });
    }, []);

    /** Column Definition for main table (memoized to avoid unnecessary DataGrid re-computation). */
    const shipmentCol = useMemo(() => {
        const getColumnsFn = getColumnsByType[tabValue];
        const shipmentColBase = getColumnsFn(
            shipmentList,
            hblShipmentList,
            cntrShipmentList,
            splitShipmentList,
            commentList,
            selectValue,
            statusList,
            tradePartnerOptions,
            locationOptions,
            vesselOptions,
            handleSelectionChange,
            handleEmailTemplateOpen,
            handleCommentOpen,
            handleHistoryOpen,
            handleTradePartnerChange,
            handleLocationChange,
            handleVesselChange,
            shipmentRow,
            null,
            todoSummary,
            tabValue
        );

        return shipmentColBase.map((col) => {
            /** status: 배열/객체 값을 문자열로 합쳐 정렬하는 것이 의미가 없으므로 정렬 자체를 비활성화. */
            /** type이 지정된 컬럼(date 등)은 MUI가 제공하는 기본 sortComparator를 그대로 사용. */
            const withSort =
                col.field === 'status'
                    ? { ...col, sortable: false }
                    : col.sortComparator || col.type
                    ? col
                    : { ...col, sortComparator: complexValueSortComparator };

            /** 배열/객체 값 컬럼(type 미지정, 커스텀 filterOperators 미지정)의 isEmpty/isNotEmpty 오작동 수정. */
            const withFilter =
                withSort.type || withSort.filterOperators
                    ? withSort
                    : { ...withSort, filterOperators: complexValueFilterOperators };

            if (
                !withFilter.editable ||
                withFilter.field === 'id' ||
                withFilter.field === 'actions' ||
                withFilter.type === 'actions'
            ) {
                return withFilter;
            }
            const originalEditable = withFilter.editable;
            return {
                ...withFilter,
                editable: (params) => {
                    if (params.row?.case_closure === 'Complete') return false;
                    return typeof originalEditable === 'function' ? originalEditable(params) : originalEditable;
                },
            };
        });
    }, [
        shipmentList,
        hblShipmentList,
        cntrShipmentList,
        splitShipmentList,
        commentList,
        selectValue,
        statusList,
        tradePartnerOptions,
        locationOptions,
        vesselOptions,
        handleSelectionChange,
        handleEmailTemplateOpen,
        handleCommentOpen,
        handleHistoryOpen,
        handleTradePartnerChange,
        handleLocationChange,
        handleVesselChange,
        todoSummary,
        tabValue,
    ]);

    /**
     * filterModel passed to the DataGrid (OS-585).
     * Items whose field is empty or no longer exists in shipmentCol (renamed/removed
     * columns, legacy blank rows) are dropped so MUI does not crash on `column.filterable`
     * when the filter panel opens. Only the grid prop is filtered; the stored view is untouched.
     */
    const gridFilterModel = useMemo(() => {
        const knownFields = new Set(shipmentCol.map((col) => col.field));
        const items = (filterView.filterModel?.items || []).filter(
            (item) => item?.field && knownFields.has(item.field)
        );
        return {
            ...filterView.filterModel,
            items,
            quickFilterExcludeHiddenColumns: false,
        };
    }, [filterView.filterModel, shipmentCol]);

    const handleFilterModelChange = useCallback(
        (model) => updateDataGridState('filterModel', model),
        [updateDataGridState]
    );
    const handleSortModelChange = useCallback(
        (model) => updateDataGridState('sortModel', model),
        [updateDataGridState]
    );
    const handleColumnVisibilityModelChange = useCallback(
        (model) => updateDataGridState('columnVisibilityModel', model),
        [updateDataGridState]
    );
    const getRowClassName = useCallback((params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'), []);
    const handleProcessRowUpdateError = useCallback(
        (error) => {
            dispatch(
                setSnackbar({
                    open: true,
                    message: error.message,
                    severity: 'error',
                })
            );
        },
        [dispatch]
    );

    const isGroupExpandedByDefault = useCallback(() => true, []);

    /** Row height must track user-entered content and text must never clip, so rows
     * use 'auto' DOM measurement as the guarantee. What made 'auto' jumpy before was
     * the flat default estimate (52px) for unmeasured rows; estimating each row from
     * the same lists the renderers read (houses, containers, shipper/consignee
     * partners) closes that estimate/measure gap. */
    const rowContentLines = useMemo(() => {
        const masterKey = mainConfig.keyName[0];
        const houseKey = mainConfig.keyName[1];
        const houseCounts = new Map();
        const houseToMaster = new Map();
        (hblShipmentList ?? []).forEach((item) => {
            const masterId = item[masterKey];
            houseCounts.set(masterId, (houseCounts.get(masterId) ?? 0) + 1);
            houseToMaster.set(item.id, masterId);
        });
        const childCounts = new Map();
        (cntrShipmentList ?? []).forEach((item) => {
            const masterId = houseKey ? houseToMaster.get(item[houseKey]) : item[masterKey];
            if (masterId === undefined) return;
            childCounts.set(masterId, (childCounts.get(masterId) ?? 0) + 1);
        });
        return { houseCounts, childCounts };
    }, [hblShipmentList, cntrShipmentList, mainConfig]);

    const getRowHeight = useCallback(() => 'auto', []);

    const getEstimatedRowHeight = useCallback(
        ({ model }) => {
            const partnerLines = Math.max(
                Array.isArray(model?.il_trade_partner_id_shipper) ? model.il_trade_partner_id_shipper.length : 0,
                Array.isArray(model?.il_trade_partner_id_consignee) ? model.il_trade_partner_id_consignee.length : 0
            );
            const lines = Math.max(
                1,
                rowContentLines.houseCounts.get(model?.id) ?? 0,
                rowContentLines.childCounts.get(model?.id) ?? 0,
                partnerLines
            );
            return Math.max(ROW_BASE_HEIGHT, lines * ROW_LINE_HEIGHT);
        },
        [rowContentLines]
    );

    if (!loaded) return <CustomSkeleton2 />;

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: '84.2vh',
                }}
            >
                <StripedDataGrid
                    rows={shipmentRow}
                    columns={shipmentCol}
                    editMode="cell"
                    pinnedColumns={mainConfig.pinnedColumns}
                    disableExtendRowFullWidth
                    getRowHeight={getRowHeight}
                    getEstimatedRowHeight={getEstimatedRowHeight}
                    sx={{
                        fontSize: '11px',
                        border: 'none',
                        '& .custom-scrollpanel-header': {
                            backgroundColor: '#dfdfdf',
                        },
                        '& .custom-scrollpanel-editable-header': {
                            backgroundColor: '#dfdfdf',
                            textDecoration: 'underline',
                        },
                        '& .custom-editable-header': {
                            textDecoration: 'underline',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            border: '1px solid #dfdfdf',
                            borderRadius: '12px',
                        },
                        '& .MuiDataGrid-cell': {
                            p: 0,
                            textAlign: 'center',
                            /* Keeps the team's row spacing under 'auto' measurement:
                               single-line rows measure 55px instead of bare text height. */
                            minHeight: '55px',
                        },
                        /* Nested grids inside the detail panel share these class names, and
                           the 55px floor above overflows their shorter rows, clipping text.
                           Undo it for everything rendered within the panel. */
                        '& .MuiDataGrid-detailPanel .MuiDataGrid-cell': {
                            minHeight: 0,
                        },
                        '& .status-cell': {
                            fontSize: '11px',
                        },
                        '& .MuiButtonBase-root': {
                            p: 0,
                        },
                        '& .MuiDataGrid-detailPanel': {
                            overflow: 'visible',
                        },
                    }}
                    columnHeaderHeight={35}
                    /* Keep rows mounted a bit past the viewport (default 150px) so a remounting
                       detail panel paints its cached-height shell before scrolling into view and
                       hydrates during idle time. Larger buffers are counterproductive now that
                       remounts are cheap: they only add always-mounted heavy rows per scroll step. */
                    rowBufferPx={300}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                            excelOptions: { disableToolbarButton: true },
                        },
                    }}
                    disableDensitySelector
                    disableRowSelectionOnClick
                    disableAggregation
                    getDetailPanelHeight={getDetailPanelHeight}
                    getDetailPanelContent={getDetailPanelContent}
                    processRowUpdate={handleProcessRowUpdate}
                    filterModel={gridFilterModel}
                    onFilterModelChange={handleFilterModelChange}
                    sortModel={filterView.sortModel}
                    onSortModelChange={handleSortModelChange}
                    columnVisibilityModel={{
                        ...filterView.columnVisibilityModel,
                        __quick_search: false,
                    }}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    onCellEditStart={handleCellEditStart}
                    experimentalFeatures={{ newEditingApi: true }}
                    detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                    onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                    apiRef={apiRef}
                    onCellKeyDown={handleTabKeyDown(apiRef)}
                    isGroupExpandedByDefault={isGroupExpandedByDefault}
                    getRowClassName={getRowClassName}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    loading={gridLoading}
                />
                <EmailBuilderDialog
                    emailTemplateOpen={emailTemplateOpen}
                    setEmailTemplateOpen={setEmailTemplateOpen}
                    selectedRow={selectedRow}
                />
                <Comment commentOpen={commentOpen} setCommentOpen={setCommentOpen} row={selectedRow} />
                <MainHistoryDialog
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    row={selectedRow?.[0]}
                    tabValue={tabValue}
                />
            </Box>
        </>
    );
}
